import { useEffect, useRef, useState } from 'react';
import { useTransactionStore } from '@/store/transactionsStore';
import { useAccountsStore } from '@/store/accountsStore';
import { useCategoriesStore } from '@/store/categoriesStore';
import { usePayeesStore } from '@/store/payeesStore';
import { useTagsStore } from '@/store/tagsStore';
import { useAuthStore } from '@/store/authStore';
import { Tag, CreditCard, Check, ChevronDown, User, Tags, DollarSign, X, RotateCcw, Calendar, Filter } from 'lucide-react';
import { Combobox } from '@headlessui/react';
import { Fragment } from 'react';
import { Transition } from '@headlessui/react';
import { Portal } from '@headlessui/react';
import { useTranslation } from 'react-i18next';
import { Subcategory } from '@/types/category';
import { toast } from 'sonner';

// Note: This component has a dual-purpose design:
// 1. It can work directly with the transaction store (default behavior)
// 2. It can accept filters and setFilters props to override the store behavior
// Type discrepancies between store filters and TransactionFilterType need to be resolved
interface TransactionFiltersProps {
  filters?: any; // Using any to bypass type issues during migration
  setFilters?: (filters: any) => void; // Using any to bypass type issues during migration
}

const TransactionFilters = ({ filters: propFilters, setFilters: propSetFilters }: TransactionFiltersProps = {}) => {
  const { t } = useTranslation();

  // Initialize customDateRange from filters if available
  const [customDateRange, setCustomDateRange] = useState({
    from: propFilters?.customDateRange?.from || '',
    to: propFilters?.customDateRange?.to || ''
  });

  // Use props if provided, otherwise use the store
  const storeValues = useTransactionStore();
  const filters = propFilters || storeValues.filters;
  const setFilters = propSetFilters || storeValues.setFilters;
  const filtersInitialized = storeValues.filtersInitialized;

  useEffect(() => {
    // Initialize customDateRange when filters change
    if (filters.customDateRange) {
      setCustomDateRange({
        from: filters.customDateRange.from || '',
        to: filters.customDateRange.to || ''
      });
    }
  }, [filters, propFilters, propSetFilters, filtersInitialized]);

  const { user } = useAuthStore();
  const { accounts } = useAccountsStore();
  const { categories, fetchCategories } = useCategoriesStore();
  const { payees, fetchPayees } = usePayeesStore();
  const { tags, fetchTags } = useTagsStore();
  const { transactions, fetchTransactions } = useTransactionStore();
  const [payeeQuery, setPayeeQuery] = useState('');
  const [tagQuery, setTagQuery] = useState('');
  const [categoryQuery, setCategoryQuery] = useState('');

  // Add state for combobox open states
  const [isPayeeOpen, setIsPayeeOpen] = useState(false);
  const [isCategoryOpen, setIsCategoryOpen] = useState(false);
  const [isTagsOpen, setIsTagsOpen] = useState(false);

  const categoryButtonRef = useRef<HTMLDivElement>(null);
  const payeeButtonRef = useRef<HTMLDivElement>(null);
  const tagsButtonRef = useRef<HTMLDivElement>(null);

  const [categoryPosition, setCategoryPosition] = useState({ top: 0, left: 0, width: 0 });
  const [payeePosition, setPayeePosition] = useState({ top: 0, left: 0, width: 0 });
  const [tagsPosition, setTagsPosition] = useState({ top: 0, left: 0, width: 0 });

  useEffect(() => {
    if (isCategoryOpen && categoryButtonRef.current) {
      const rect = categoryButtonRef.current.getBoundingClientRect();
      setCategoryPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width
      });
    }
  }, [isCategoryOpen]);

  useEffect(() => {
    if (isPayeeOpen && payeeButtonRef.current) {
      const rect = payeeButtonRef.current.getBoundingClientRect();
      setPayeePosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width
      });
    }
  }, [isPayeeOpen]);

  useEffect(() => {
    if (isTagsOpen && tagsButtonRef.current) {
      const rect = tagsButtonRef.current.getBoundingClientRect();
      setTagsPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width
      });
    }
  }, [isTagsOpen]);

  useEffect(() => {
    if (user?.id) {
      fetchCategories(user.id);
      fetchPayees(user.id);
      fetchTags(user.id);
    }
  }, [user?.id, fetchCategories, fetchPayees, fetchTags]);

  useEffect(() => {
    if (filters.dateRange !== 'custom') {
      setCustomDateRange({ from: '', to: '' });
    }
  }, [filters.dateRange]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.combobox-container')) {
        setIsCategoryOpen(false);
        setIsPayeeOpen(false);
        if (!filters.tags?.length) {
          setIsTagsOpen(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [filters.tags]);

  const handleCustomDateChange = (field: 'from' | 'to', value: string) => {
    const newDateRange = { ...customDateRange, [field]: value };
    setCustomDateRange(newDateRange);

    // Update filters when both dates are selected
    if (newDateRange.from && newDateRange.to) {
      setFilters({
        ...filters,
        dateRange: 'custom',
        customDateRange: {
          from: newDateRange.from,
          to: newDateRange.to
        }
      });
    }
  };

  // Filter payees based on search query
  const filteredPayees = payeeQuery === ''
    ? payees
    : payees.filter(payee =>
        payee.name.toLowerCase().replace(/\s+/g, '').includes(payeeQuery.toLowerCase().replace(/\s+/g, ''))
      );

  // Filter tags based on search query
  const filteredTags = tagQuery === ''
    ? tags
    : tags.filter(tag =>
        tag.name.toLowerCase().replace(/\s+/g, '').includes(tagQuery.toLowerCase().replace(/\s+/g, ''))
      );

  // Filter categories based on search query
  const filteredCategories = categoryQuery === ''
    ? categories
    : categories.flatMap(category => {
        const matchingSubcategories = category.subcategories?.filter((subcategory: Subcategory) =>
          `${category.name} - ${subcategory.name}`
            .toLowerCase()
            .replace(/\s+/g, '')
            .includes(categoryQuery.toLowerCase().replace(/\s+/g, ''))
        ) || [];

        if (category.name.toLowerCase().includes(categoryQuery.toLowerCase()) || matchingSubcategories.length > 0) {
          return [{
            ...category,
            subcategories: matchingSubcategories
          }];
        }
        return [];
      });

  const handleFilterChange = (key: string, value: any) => {
    let newFilters = { ...filters };

    // Special handling for date range
    if (key === 'dateRange') {
      // If switching to custom, initialize custom date range if it doesn't exist
      if (value === 'custom') {
        if (!filters.customDateRange || !filters.customDateRange.from || !filters.customDateRange.to) {
          const now = new Date();
          const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
          
          newFilters = {
            ...newFilters,
            [key]: value,
            customDateRange: {
              from: firstOfMonth.toISOString().split('T')[0], // Format as YYYY-MM-DD
              to: now.toISOString().split('T')[0] // Format as YYYY-MM-DD
            }
          };
        } else {
          // Keep existing custom date range
          newFilters[key] = value;
        }
      } else {
        // If switching to a non-custom option, keep the custom date range for later
        // but set the active dateRange to the new value
        newFilters[key] = value;
      }
    } 
    // Special handling for custom date range
    else if (key === 'customDateRange') {
      newFilters = {
        ...newFilters,
        dateRange: 'custom', // Ensure dateRange is set to custom
        [key]: value
      };
    }
    // Special handling for category
    else if (key === 'category') {
      // Validate the value - null, undefined, empty string, or 'all' should be normalized to 'all'
      const categoryValue = (!value || value === '') ? 'all' : value;
      newFilters[key] = categoryValue;
    }
    // Special handling for payee
    else if (key === 'payee') {
      // Validate the value - null, undefined, empty string, or 'all' should be normalized to 'all'
      const payeeValue = (!value || value === '') ? 'all' : value;
      newFilters[key] = payeeValue;
    }
    // Special handling for accounts
    else if (key === 'accounts') {
      // Ensure we have a clean array without empty strings
      const validAccounts = Array.isArray(value) 
        ? value.filter((id: string | null | undefined) => id !== null && id !== undefined && id !== '')
        : (value ? [value].filter((id: string | null | undefined) => id !== null && id !== undefined && id !== '') : []);
      
      // If we have existing accounts, handle toggling
      if (Array.isArray(filters.accounts) && filters.accounts.length > 0) {
        // Filter out null/undefined/empty values from the current accounts
        const cleanAccounts = filters.accounts.filter((id: string | null | undefined) => 
          id !== null && id !== undefined && id !== '');
        
        // If toggling a single account
        if (typeof value === 'string' && value) {
          // If the account is already in the array, remove it, otherwise add it
          const accountExists = cleanAccounts.some((id: string) => String(id) === String(value));
          
          if (accountExists) {
            // Remove the account
            newFilters.accounts = cleanAccounts.filter((id: string) => String(id) !== String(value));
          } else {
            // Add the account
            newFilters.accounts = [...cleanAccounts, value];
          }
        } else {
          // Replace with the new accounts
          newFilters.accounts = validAccounts;
        }
      } else {
        // No existing accounts, just set the new ones
        newFilters.accounts = validAccounts;
      }
    } 
    // Default handling for all other filter types
    else {
      newFilters[key] = value;
    }
    
    setFilters(newFilters);
  };

  // Add handlers for clearing values
  const handleClearPayee = () => {
    handleFilterChange('payee', 'all');
    setPayeeQuery('');
  };

  const handleClearCategory = () => {
    handleFilterChange('category', 'all');
    setCategoryQuery('');
  };

  const handleCategoryChange = (value: string) => {
    // If value is empty or invalid, use 'all'
    if (!value || value === '') {
      handleFilterChange('category', 'all');
      setIsCategoryOpen(false);
      return;
    }
    
    // Handle special "none" value for transactions with no category
    if (value === 'none') {
      // Force special filter that won't match any real category ID
      
      // First check if we have transactions to filter
      if (!transactions.length) {
        toast.info('Loading transactions before filtering...', {
          description: 'Please wait while we fetch your transaction data.',
          duration: 3000
        });
        if (user?.id) {
          fetchTransactions(user.id, {
            page: 1,
            pageSize: 100,
            orderBy: 'date',
            orderDirection: 'desc'
          }).then(() => {
            handleFilterChange('category', 'none');
          }).catch(error => {
            toast.error('Failed to load transactions');
          });
          return;
        }
      }
      
      handleFilterChange('category', 'none');
      setIsCategoryOpen(false);
      return;
    }
    
    handleFilterChange('category', value);
    setIsCategoryOpen(false);
  };

  const handlePayeeChange = (value: string) => {
    // If value is empty, clear the filter
    if (!value || value === '') {
      handleFilterChange('payee', 'all');
      setIsPayeeOpen(false);
      return;
    }
    
    // Handle special "all" value
    if (value === 'all') {
      handleFilterChange('payee', 'all');
      setIsPayeeOpen(false);
      return;
    }
    
    // Handle special "none" value for transactions with no payee
    if (value === 'none') {
      // First check if we have transactions to filter
      if (!transactions.length) {
        toast.info('Loading transactions before filtering...', {
          description: 'Please wait while we fetch your transaction data.',
          duration: 3000
        });
        if (user?.id) {
          fetchTransactions(user.id, {
            page: 1,
            pageSize: 100,
            orderBy: 'date',
            orderDirection: 'desc'
          }).then(() => {
            handleFilterChange('payee', 'none');
          }).catch(error => {
            toast.error('Failed to load transactions');
          });
          return;
        }
      }
      
      handleFilterChange('payee', 'none');
      setIsPayeeOpen(false);
      return;
    }

    // Find the selected payee
    const selectedPayee = payees.find(p => p.id === value);
    if (selectedPayee) {
      // If we found a payee, use its ID
      handleFilterChange('payee', selectedPayee.id);
    } else {
      // If no payee found, use the value as is (it might be a direct name)
      handleFilterChange('payee', value);
    }
    setIsPayeeOpen(false);
  };

  const getPayeeDisplayValue = (value: string) => {
    if (!value || value === 'all') return '';
    if (value === 'none') return t('transactions.filters.noPayee');
    
    // Try to find the payee by ID first
    const payee = payees.find(p => p.id === value);
    if (payee) return payee.name;
    
    // If not found, return the value as is (it might be a direct name)
    return value;
  };

  // Add resetFilters function
  const resetFilters = () => {
    setFilters({
      dateRange: 'all',
      customDateRange: undefined,
      type: 'all',
      accounts: [],
      category: 'all',
      payee: 'all',
      tags: []
    });
    setCustomDateRange({ from: '', to: '' });
    setCategoryQuery('');
    setPayeeQuery('');
    setTagQuery('');
  };

  // Count active filters for display
  const getActiveFiltersCount = () => {
    let count = 0;
    if (filters.dateRange && filters.dateRange !== 'all') count++;
    if (filters.type && filters.type !== 'all') count++;
    if (filters.accounts && filters.accounts.length > 0) count++;
    if (filters.category && filters.category !== 'all') count++;
    if (filters.payee && filters.payee !== 'all') count++;
    if (filters.tags && filters.tags.length > 0) count++;
    if (filters.search && filters.search.trim() !== '') count++;
    return count;
  };

  const activeFiltersCount = getActiveFiltersCount();

  return (
    <div className="bg-background-secondary/80 dark:bg-background-dark-secondary/80 backdrop-blur-xl backdrop-saturate-150 border border-border dark:border-border-dark rounded-xl shadow-sm">
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b border-border/30 dark:border-border-dark/30 bg-gradient-to-r from-primary/5 to-secondary/5 dark:from-primary-dark/5 dark:to-secondary-dark/5 rounded-t-xl">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-primary/10 dark:bg-primary-dark/10">
            <Filter className="w-5 h-5 text-primary dark:text-primary-dark" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary">
              {t('transactions.filters.title', 'Filters')}
            </h3>
            {activeFiltersCount > 0 && (
              <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
                {activeFiltersCount} {activeFiltersCount === 1 ? 'filter' : 'filters'} applied
              </p>
            )}
          </div>
        </div>
        
        {activeFiltersCount > 0 && (
          <button
            onClick={resetFilters}
            className="flex items-center gap-2 px-3 py-1.5 text-sm bg-background-secondary dark:bg-background-dark-secondary border border-border dark:border-border-dark rounded-lg hover:bg-secondary/10 hover:text-secondary dark:hover:bg-primary-dark/10 dark:hover:text-primary-dark transition-all duration-200"
          >
            <RotateCcw className="w-4 h-4" />
            Reset
          </button>
        )}
      </div>

      {/* Filter Content */}
      <div className="p-6 space-y-6">
        {/* First Row - Date, Type, Account */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Date Range Filter */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary">
              <Calendar className="w-4 h-4 inline mr-2" />
              {t('transactions.filters.dateRange')}
            </label>
            <select
              className="w-full h-11 bg-white/80 dark:bg-background-dark-secondary/80 border border-border dark:border-border-dark rounded-xl px-4 py-2 text-text-primary dark:text-text-dark-primary outline-none focus:ring-2 focus:ring-primary/30 dark:focus:ring-primary-dark/30 focus:border-primary dark:focus:border-primary-dark cursor-pointer backdrop-blur-sm transition-all duration-200"
              value={filters.dateRange || 'all'}
              onChange={(e) => handleFilterChange('dateRange', e.target.value)}
            >
              <option value="all">{t('transactions.filters.allTime')}</option>
              <option value="today">{t('transactions.filters.today')}</option>
              <option value="yesterday">{t('transactions.filters.yesterday')}</option>
              <option value="thisWeek">{t('transactions.filters.thisWeek')}</option>
              <option value="lastWeek">{t('transactions.filters.lastWeek')}</option>
              <option value="thisMonth">{t('transactions.filters.thisMonth')}</option>
              <option value="lastMonth">{t('transactions.filters.lastMonth')}</option>
              <option value="thisQuarter">{t('transactions.filters.thisQuarter')}</option>
              <option value="lastQuarter">{t('transactions.filters.lastQuarter')}</option>
              <option value="thisYear">{t('transactions.filters.thisYear')}</option>
              <option value="lastYear">{t('transactions.filters.lastYear')}</option>
              <option value="custom">{t('transactions.filters.customRange')}</option>
            </select>

            {filters.dateRange === 'custom' && (
              <div className="grid grid-cols-2 gap-2 mt-2">
                <input
                  type="date"
                  className="h-10 bg-white/80 dark:bg-background-dark-secondary/80 border border-border dark:border-border-dark rounded-lg px-3 py-2 text-text-primary dark:text-text-dark-primary outline-none focus:ring-2 focus:ring-primary/30 dark:focus:ring-primary-dark/30 focus:border-primary dark:focus:border-primary-dark backdrop-blur-sm transition-all duration-200"
                  value={customDateRange.from}
                  onChange={(e) => handleCustomDateChange('from', e.target.value)}
                />
                <input
                  type="date"
                  className="h-10 bg-white/80 dark:bg-background-dark-secondary/80 border border-border dark:border-border-dark rounded-lg px-3 py-2 text-text-primary dark:text-text-dark-primary outline-none focus:ring-2 focus:ring-primary/30 dark:focus:ring-primary-dark/30 focus:border-primary dark:focus:border-primary-dark backdrop-blur-sm transition-all duration-200"
                  value={customDateRange.to}
                  onChange={(e) => handleCustomDateChange('to', e.target.value)}
                />
              </div>
            )}
          </div>

          {/* Type Filter */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary">
              <DollarSign className="w-4 h-4 inline mr-2" />
              {t('transactions.filters.type')}
            </label>
            <div className="relative">
              <select
                className="w-full h-11 bg-white/80 dark:bg-background-dark-secondary/80 border border-border dark:border-border-dark rounded-xl px-4 py-2 text-text-primary dark:text-text-dark-primary outline-none focus:ring-2 focus:ring-primary/30 dark:focus:ring-primary-dark/30 focus:border-primary dark:focus:border-primary-dark cursor-pointer backdrop-blur-sm transition-all duration-200 appearance-none"
                value={filters.type || 'all'}
                onChange={(e) => handleFilterChange('type', e.target.value)}
              >
                <option value="all">{t('transactions.filters.allTypes')}</option>
                <option value="income">{t('transactions.types.income')}</option>
                <option value="expense">{t('transactions.types.expense')}</option>
                <option value="transfer">{t('transactions.types.transfer')}</option>
              </select>
              <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-text-secondary dark:text-text-dark-secondary pointer-events-none" />
            </div>
          </div>

          {/* Account Filter */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary">
              <CreditCard className="w-4 h-4 inline mr-2" />
              {t('transactions.filters.account')}
            </label>
            <div className="relative">
              <select
                className="w-full h-11 bg-white/80 dark:bg-background-dark-secondary/80 border border-border dark:border-border-dark rounded-xl px-4 py-2 text-text-primary dark:text-text-dark-primary outline-none focus:ring-2 focus:ring-primary/30 dark:focus:ring-primary-dark/30 focus:border-primary dark:focus:border-primary-dark cursor-pointer backdrop-blur-sm transition-all duration-200 appearance-none"
                value={(filters.accounts && filters.accounts[0]) || ''}
                onChange={(e) => {
                  const accountId = e.target.value;
                  handleFilterChange('accounts', accountId ? [accountId] : []);
                }}
              >
                <option value="">{t('transactions.filters.allAccounts')}</option>
                {[...accounts]
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((account) => (
                    <option key={account.id} value={account.id}>
                      {account.name}
                    </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-text-secondary dark:text-text-dark-secondary pointer-events-none" />
            </div>
          </div>
        </div>

        {/* Second Row - Category, Payee, Tags */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Category Combobox */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary">
              <Tag className="w-4 h-4 inline mr-2" />
              {t('transactions.filters.category')}
            </label>
            <Combobox
              as="div"
              value={filters.category === '' ? 'all' : filters.category || 'all'}
              onChange={handleCategoryChange}
              className="relative combobox-container"
            >
              <div className="relative">
                <div
                  ref={categoryButtonRef}
                  className="flex items-center h-11 bg-white/80 dark:bg-background-dark-secondary/80 border border-border dark:border-border-dark rounded-xl px-4 cursor-pointer hover:ring-2 hover:ring-primary/30 dark:hover:ring-primary-dark/30 hover:border-primary dark:hover:border-primary-dark transition-all duration-200 backdrop-blur-sm"
                  onClick={() => setIsCategoryOpen(true)}
                >
                  <Tag className="w-4 h-4 text-text-secondary dark:text-text-dark-secondary mr-2 flex-shrink-0" />
                  <Combobox.Input
                    className="bg-transparent flex-1 outline-none border-none text-text-primary dark:text-text-dark-primary placeholder:text-text-secondary dark:placeholder:text-text-dark-secondary"
                    placeholder={t('transactions.filters.search')}
                    onChange={(e) => setCategoryQuery(e.target.value)}
                    displayValue={(categoryId: string) => {
                      if (!categoryId || categoryId === 'all') return '';
                      
                      // Handle special "none" case
                      if (categoryId === 'none') {
                        return t('transactions.filters.noCategory');
                      }
                      
                      // Find the category by ID
                      for (const category of categories) {
                        if (category.id === categoryId) {
                          return category.name;
                        }
                        // Check subcategories
                        const subcategory = category.subcategories?.find((sub: Subcategory) => sub.id === categoryId);
                        if (subcategory) {
                          return `${category.name} - ${subcategory.name}`;
                        }
                      }
                      return '';
                    }}
                  />
                  {categoryQuery && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setCategoryQuery('');
                      }}
                      className="text-text-secondary hover:text-text-primary dark:text-text-dark-secondary dark:hover:text-text-dark-primary transition-colors mr-1"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                  {filters.category && filters.category !== 'all' && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleClearCategory();
                      }}
                      className="text-text-secondary hover:text-text-primary dark:text-text-dark-secondary dark:hover:text-text-dark-primary transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                  <ChevronDown className="w-4 h-4 text-text-secondary dark:text-text-dark-secondary ml-2 flex-shrink-0" />
                </div>
              </div>

              <Transition
                show={isCategoryOpen}
                as={Fragment}
                leave="transition ease-in duration-100"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <Portal>
                  <Combobox.Options
                    static
                    className="absolute z-[9999] w-full bg-white/95 dark:bg-background-dark-secondary/95 backdrop-blur-xl backdrop-saturate-150 border border-border dark:border-border-dark rounded-xl mt-1 py-2 text-base shadow-lg max-h-60 overflow-auto focus:outline-none"
                    style={{
                      top: categoryPosition.top,
                      left: categoryPosition.left,
                      width: categoryPosition.width,
                    }}
                  >
                    {filteredCategories.length === 0 && (
                      <div className="px-4 py-2 text-sm text-text-secondary dark:text-text-dark-secondary">
                        {t('transactions.filters.noCategoriesFound')}
                      </div>
                    )}
                    <div className="px-4 py-2 cursor-pointer hover:bg-primary/10 dark:hover:bg-primary-dark/10 rounded-lg mx-2 transition-colors" onClick={() => handleCategoryChange('none')}>
                      {t('transactions.filters.noCategory')}
                    </div>
                    <div className="border-t border-border/30 dark:border-border-dark/30 my-1 mx-2"></div>
                    {filteredCategories.map((category) => (
                      <div key={category.id}>
                        {/* Main Category Option */}
                        <Combobox.Option
                          value={category.id}
                          className={({ active }) =>
                            `relative cursor-pointer select-none py-2 px-4 mx-2 rounded-lg font-medium transition-colors ${
                              active ? 'bg-primary/15 text-primary dark:bg-primary-dark/15 dark:text-primary-dark' : 'text-text-primary dark:text-text-dark-primary bg-secondary/5 dark:bg-secondary-dark/5'
                            }`
                          }
                        >
                          {({ selected, active }) => (
                            <>
                              <span className={`block truncate ${selected ? 'font-bold' : 'font-medium'}`}>
                                {category.name}
                              </span>
                              {selected ? (
                                <span
                                  className={`absolute inset-y-0 right-0 flex items-center pr-4 ${
                                    active ? 'text-white' : 'text-primary dark:text-primary-dark'
                                  }`}
                                >
                                  <Check className="h-4 w-4" aria-hidden="true" />
                                </span>
                              ) : null}
                            </>
                          )}
                        </Combobox.Option>
                        {/* Subcategories */}
                        {category.subcategories?.map((subcategory: Subcategory) => (
                          <Combobox.Option
                            key={subcategory.id}
                            value={subcategory.id}
                            className={({ active }) =>
                              `relative cursor-pointer select-none py-2 pl-10 pr-4 mx-2 rounded-lg transition-colors ${
                                active ? 'bg-primary/15 text-primary dark:bg-primary-dark/15 dark:text-primary-dark' : 'text-text-primary dark:text-text-dark-primary'
                              }`
                            }
                          >
                            {({ selected, active }) => (
                              <>
                                <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                                  {subcategory.name}
                                </span>
                                {selected ? (
                                  <span
                                    className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                                      active ? 'text-white' : 'text-primary dark:text-primary-dark'
                                    }`}
                                  >
                                    <Check className="h-4 w-4" aria-hidden="true" />
                                  </span>
                                ) : null}
                              </>
                            )}
                          </Combobox.Option>
                        ))}
                      </div>
                    ))}
                  </Combobox.Options>
                </Portal>
              </Transition>
            </Combobox>
          </div>

          {/* Payee Combobox */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary">
              <User className="w-4 h-4 inline mr-2" />
              {t('transactions.filters.payee')}
            </label>
            <Combobox
              as="div"
              value={filters.payee === '' ? 'all' : filters.payee || 'all'}
              onChange={handlePayeeChange}
              className="relative combobox-container"
            >
              <div className="relative">
                <div
                  ref={payeeButtonRef}
                  className="flex items-center h-11 bg-white/80 dark:bg-background-dark-secondary/80 border border-border dark:border-border-dark rounded-xl px-4 cursor-pointer hover:ring-2 hover:ring-primary/30 dark:hover:ring-primary-dark/30 hover:border-primary dark:hover:border-primary-dark transition-all duration-200 backdrop-blur-sm"
                  onClick={() => setIsPayeeOpen(true)}
                >
                  <User className="w-4 h-4 text-text-secondary dark:text-text-dark-secondary mr-2 flex-shrink-0" />
                  <Combobox.Input
                    className="bg-transparent flex-1 outline-none border-none text-text-primary dark:text-text-dark-primary placeholder:text-text-secondary dark:placeholder:text-text-dark-secondary"
                    placeholder={t('transactions.filters.search')}
                    onChange={(e) => setPayeeQuery(e.target.value)}
                    displayValue={getPayeeDisplayValue}
                  />
                  {payeeQuery && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setPayeeQuery('');
                      }}
                      className="text-text-secondary hover:text-text-primary dark:text-text-dark-secondary dark:hover:text-text-dark-primary transition-colors mr-1"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                  {filters.payee && filters.payee !== 'all' && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleClearPayee();
                      }}
                      className="text-text-secondary hover:text-text-primary dark:text-text-dark-secondary dark:hover:text-text-dark-primary transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                  <ChevronDown className="w-4 h-4 text-text-secondary dark:text-text-dark-secondary ml-2 flex-shrink-0" />
                </div>
              </div>

              <Transition
                show={isPayeeOpen}
                as={Fragment}
                leave="transition ease-in duration-100"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <Portal>
                  <Combobox.Options
                    static
                    className="absolute z-[9999] w-full bg-white/95 dark:bg-background-dark-secondary/95 backdrop-blur-xl backdrop-saturate-150 border border-border dark:border-border-dark rounded-xl mt-1 py-2 text-base shadow-lg max-h-60 overflow-auto focus:outline-none"
                    style={{
                      top: payeePosition.top,
                      left: payeePosition.left,
                      width: payeePosition.width,
                    }}
                  >
                    {filteredPayees.length === 0 ? (
                      <div className="px-4 py-2 text-sm text-text-secondary dark:text-text-dark-secondary">
                        {t('transactions.filters.noPayeesFound')}
                      </div>
                    ) : (
                      <>
                        <div className="px-4 py-2 cursor-pointer hover:bg-primary/10 dark:hover:bg-primary-dark/10 rounded-lg mx-2 transition-colors" onClick={() => handlePayeeChange('all')}>
                          {t('transactions.filters.allPayees')}
                        </div>
                        <div className="px-4 py-2 cursor-pointer hover:bg-primary/10 dark:hover:bg-primary-dark/10 rounded-lg mx-2 transition-colors" onClick={() => handlePayeeChange('none')}>
                          {t('transactions.filters.noPayee')}
                        </div>
                        <div className="border-t border-border/30 dark:border-border-dark/30 my-1 mx-2"></div>
                        {filteredPayees.map((payee) => (
                          <Combobox.Option
                            key={payee.id}
                            value={payee.id}
                            className={({ active }) =>
                              `relative cursor-pointer select-none py-2 px-4 mx-2 rounded-lg transition-colors ${
                                active ? 'bg-primary/15 text-primary dark:bg-primary-dark/15 dark:text-primary-dark' : 'text-text-primary dark:text-text-dark-primary'
                              }`
                            }
                          >
                            {({ selected, active }) => (
                              <>
                                <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                                  {payee.name}
                                </span>
                                {selected ? (
                                  <span
                                    className={`absolute inset-y-0 right-0 flex items-center pr-4 ${
                                      active ? 'text-white' : 'text-primary dark:text-primary-dark'
                                    }`}
                                  >
                                    <Check className="w-4 h-4" />
                                  </span>
                                ) : null}
                              </>
                            )}
                          </Combobox.Option>
                        ))}
                      </>
                    )}
                  </Combobox.Options>
                </Portal>
              </Transition>
            </Combobox>
          </div>

          {/* Tags Filter */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary">
              <Tags className="w-4 h-4 inline mr-2" />
              {t('transactions.filters.tags')}
            </label>
            <Combobox
              as="div"
              multiple
              value={filters.tags || []}
              onChange={(value) => {
                handleFilterChange('tags', value);
                setIsTagsOpen(false);
              }}
              className="relative combobox-container"
            >
              <div className="relative">
                <div
                  ref={tagsButtonRef}
                  className="flex items-center h-11 bg-white/80 dark:bg-background-dark-secondary/80 border border-border dark:border-border-dark rounded-xl px-4 cursor-pointer hover:ring-2 hover:ring-primary/30 dark:hover:ring-primary-dark/30 hover:border-primary dark:hover:border-primary-dark transition-all duration-200 backdrop-blur-sm"
                  onClick={() => setIsTagsOpen(true)}
                >
                  <Tags className="w-4 h-4 text-text-secondary dark:text-text-dark-secondary mr-2 flex-shrink-0" />
                  <div className="flex-1 flex items-center overflow-hidden">
                    {filters.tags && filters.tags.length > 0 ? (
                      <div className="flex items-center gap-1.5 overflow-hidden">
                        {filters.tags.slice(0, 2).map((tagId: string) => {
                          const tag = tags.find((t) => t.id === tagId);
                          return tag ? (
                            <span
                              key={tag.id}
                              className="inline-flex items-center px-2 py-0.5 rounded-lg text-xs font-medium backdrop-blur-sm flex-shrink-0"
                              style={{
                                backgroundColor: tag.color + '20',
                                color: tag.color,
                                border: `1px solid ${tag.color}40`
                              }}
                            >
                              {tag.name}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleFilterChange(
                                    'tags',
                                    filters.tags?.filter((tagId: string) => tagId !== tag.id) || []
                                  );
                                }}
                                className="ml-1 hover:opacity-70 transition-opacity"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </span>
                          ) : null;
                        })}
                        {filters.tags.length > 2 && (
                          <span className="text-xs text-text-secondary dark:text-text-dark-secondary px-2 py-0.5 bg-background-secondary/50 dark:bg-background-dark-secondary/50 rounded-lg flex-shrink-0">
                            +{filters.tags.length - 2} more
                          </span>
                        )}
                      </div>
                    ) : (
                      <div className="flex-1 flex items-center">
                        <Combobox.Input
                          className="bg-transparent flex-1 outline-none border-none text-text-primary dark:text-text-dark-primary placeholder:text-text-secondary dark:placeholder:text-text-dark-secondary"
                          placeholder={t('transactions.filters.search')}
                          onChange={(e) => setTagQuery(e.target.value)}
                          onClick={() => setIsTagsOpen(true)}
                        />
                        {tagQuery && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setTagQuery('');
                            }}
                            className="text-text-secondary hover:text-text-primary dark:text-text-dark-secondary dark:hover:text-text-dark-primary transition-colors flex-shrink-0"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  <ChevronDown className="w-4 h-4 text-text-secondary dark:text-text-dark-secondary ml-2 flex-shrink-0" />
                </div>
              </div>

              <Transition
                show={isTagsOpen}
                as={Fragment}
                leave="transition ease-in duration-100"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <Portal>
                  <Combobox.Options
                    static
                    className="absolute z-[9999] w-full bg-white/95 dark:bg-background-dark-secondary/95 backdrop-blur-xl backdrop-saturate-150 border border-border dark:border-border-dark rounded-xl mt-1 py-2 text-base shadow-lg max-h-60 overflow-auto focus:outline-none"
                    style={{
                      top: tagsPosition.top,
                      left: tagsPosition.left,
                      width: tagsPosition.width,
                    }}
                  >
                    {filteredTags.length === 0 && tagQuery !== '' ? (
                      <div className="px-4 py-2 text-text-secondary dark:text-text-dark-secondary">
                        {t('transactions.filters.noResults')}
                      </div>
                    ) : (
                      filteredTags.map((tag) => (
                        <Combobox.Option
                          key={tag.id}
                          value={tag.id}
                          className={({ active }) =>
                            `relative cursor-pointer py-2 pl-3 pr-9 mx-2 rounded-lg transition-colors ${
                              active ? 'bg-primary/10 dark:bg-primary-dark/10' : ''
                            }`
                          }
                        >
                          {({ selected }) => (
                            <>
                              <div className="flex items-center gap-2">
                                <div
                                  className="w-3 h-3 rounded-full"
                                  style={{ backgroundColor: tag.color }}
                                ></div>
                                <span
                                  className={`block truncate ${
                                    selected ? 'font-medium' : 'font-normal'
                                  }`}
                                >
                                  {tag.name}
                                </span>
                              </div>
                              {filters.tags?.includes(tag.id) && (
                                <span
                                  className="absolute inset-y-0 right-0 flex items-center pr-4 text-primary dark:text-primary-dark"
                                >
                                  <Check className="w-4 h-4" />
                                </span>
                              )}
                            </>
                          )}
                        </Combobox.Option>
                      ))
                    )}
                  </Combobox.Options>
                </Portal>
              </Transition>
            </Combobox>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TransactionFilters;